/**
 * Keyboard shortcuts for the Todo Lists arche.
 *
 * Registers global keydown listeners for undo/redo.
 * Suppresses shortcuts when input/contentEditable is focused
 * (browser handles native text undo) or a dialog is open.
 */

import { useEffect, useCallback } from 'react';

interface TodoShortcutActions {
  onUndo: () => void;
  onRedo: () => void;
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (el as HTMLElement).isContentEditable
  );
}

function isDialogOpen(): boolean {
  return document.querySelector('[role="dialog"]') !== null;
}

export function useTodoShortcuts(actions: TodoShortcutActions) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isInputFocused() || isDialogOpen()) return;

      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      const key = e.key.toLowerCase();

      // Ctrl+Z / Cmd+Z → Undo
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        actions.onUndo();
        return;
      }

      // Ctrl+Shift+Z / Cmd+Shift+Z → Redo
      if (key === 'z' && e.shiftKey) {
        e.preventDefault();
        actions.onRedo();
        return;
      }

      // Ctrl+Y → Redo (Windows convention)
      if (key === 'y') {
        e.preventDefault();
        actions.onRedo();
      }
    },
    [actions],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
